<template>
  <form @submit.prevent="submitForm">
    <FormField
      id="Clave"
      label="Clave del Proyecto"
      type="text"
      v-model="formData.Clave"
      :required="true"
      :maxlength="50"
      :disabled="isEditing"
      helpText="Clave única del proyecto (se genera automáticamente para nuevos registros)"
    />
    
    <!-- Campo EmpresaId oculto - se captura automáticamente -->
    <input type="hidden" v-model="formData.EmpresaId" />
    
    <FormField
      id="Nombre"
      label="Nombre del Proyecto"
      type="text"
      v-model="formData.Nombre"
      :required="true"
      :maxlength="200"
      helpText="Nombre del proyecto (máximo 200 caracteres)"
    />
    
    <FormField
      id="ClaveErp"
      label="Clave ERP"
      type="text"
      v-model="formData.ClaveErp"
      :maxlength="50"
      helpText="Clave del proyecto en el sistema ERP (opcional)"
    />
    
    <FormField
      id="EsActivo"
      label="Estado"
      type="checkbox"
      v-model="formData.EsActivo"
      checkboxLabel="Proyecto activo"
      helpText="Indica si el proyecto está activo en el sistema"
    />
    
    <FormField
      id="Descripcion"
      label="Descripción"
      type="textarea"
      v-model="formData.Descripcion"
      :maxlength="500"
      :rows="3"
      helpText="Descripción detallada del proyecto (máximo 500 caracteres)"
    />
    
    <FormField
      id="FechaInicio"
      label="Fecha de Inicio"
      type="date"
      v-model="formData.FechaInicio"
      helpText="Fecha de inicio del proyecto (opcional)"
    />
    
    <FormField
      id="FechaFin"
      label="Fecha de Fin"
      type="date"
      v-model="formData.FechaFin"
      helpText="Fecha de finalización del proyecto (opcional)"
    />
    
    <FormField
      id="Cliente"
      label="Cliente"
      type="text"
      v-model="formData.Cliente"
      :maxlength="200"
      helpText="Nombre del cliente del proyecto (opcional)"
    />
    
    <FormField
      id="Responsable"
      label="Responsable"
      type="text"
      v-model="formData.Responsable"
      :maxlength="200"
      helpText="Nombre del responsable del proyecto (opcional)"
    />
    
    <FormField
      id="Direccion"
      label="Dirección"
      type="text"
      v-model="formData.Direccion"
      :maxlength="300"
      helpText="Dirección del proyecto (opcional)"
    />
    
    <FormField
      id="Telefono"
      label="Teléfono"
      type="text"
      v-model="formData.Telefono"
      :maxlength="20"
      helpText="Número de teléfono de contacto (opcional)"
    />
    
    <FormField
      id="Email"
      label="Email"
      type="email"
      v-model="formData.Email"
      :maxlength="100"
      helpText="Dirección de correo electrónico de contacto (opcional)"
    />
    
    <FormField
      id="Presupuesto"
      label="Presupuesto"
      type="number"
      v-model="formData.Presupuesto"
      step="0.01"
      min="0"
      max="99999999.99"
      helpText="Presupuesto del proyecto en pesos mexicanos (opcional)"
    />
    
    <FormField
      id="CostoActual"
      label="Costo Actual"
      type="number"
      v-model="formData.CostoActual"
      step="0.01"
      min="0"
      max="99999999.99"
      helpText="Costo actual del proyecto en pesos mexicanos (opcional)"
    />
    
    <FormField
      id="PorcentajeAvance"
      label="Porcentaje de Avance"
      type="number"
      v-model="formData.PorcentajeAvance"
      step="0.01"
      min="0"
      max="100"
      helpText="Porcentaje de avance del proyecto (0-100%)"
    />
    
    <FormField
      id="Estatus"
      label="Estatus"
      type="text"
      v-model="formData.Estatus"
      :maxlength="50"
      helpText="Estatus actual del proyecto (opcional)"
    />
    
    <FormField
      id="Notas"
      label="Notas"
      type="textarea"
      v-model="formData.Notas"
      :maxlength="1000"
      :rows="3"
      helpText="Notas adicionales del proyecto (máximo 1000 caracteres)"
    />
    
    <div class="d-flex gap-2 mt-4">
      <button type="button" class="btn btn-primary" :disabled="loading" @click="submitForm">
        <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        <i v-else class="bi bi-save me-1"></i>
        {{ isEditing ? 'Actualizar' : 'Guardar' }}
      </button>
      <button type="button" class="btn btn-secondary" @click="$emit('cancel')">
        <i class="bi bi-x-circle me-1"></i> Cancelar
      </button>
    </div>
  </form>
</template>

<script>
import FormField from '@/shared/components/FormField.vue';
import empresaMixin from '@/shared/mixins/empresaMixin';

export default {
  name: 'CatProyectoForm',
  components: {
    FormField
  },
  mixins: [empresaMixin],
  props: {
    proyecto: {
      type: Object,
      default: () => ({
        Clave: '',
        EmpresaId: null,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true,
        Descripcion: '',
        FechaInicio: '',
        FechaFin: '',
        Cliente: '',
        Responsable: '',
        Direccion: '',
        Telefono: '',
        Email: '',
        Presupuesto: null,
        CostoActual: null,
        PorcentajeAvance: null,
        Estatus: '',
        Notas: ''
      })
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: { ...this.proyecto }
    };
  },
  watch: {
    proyecto: {
      handler(newValue) {
        this.formData = { ...newValue };
        // Formatear fechas para mostrar en formato local
        if (this.formData.FechaInicio) {
          const date = new Date(this.formData.FechaInicio);
          if (!isNaN(date.getTime())) {
            this.formData.FechaInicio = date.toISOString().split('T')[0];
          }
        }
        
        if (this.formData.FechaFin) {
          const date = new Date(this.formData.FechaFin);
          if (!isNaN(date.getTime())) {
            this.formData.FechaFin = date.toISOString().split('T')[0];
          }
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    generateCode(proyectos) {
      // Generar código secuencial basado en los proyectos existentes
      if (!proyectos || proyectos.length === 0) {
        return 'PR001';
      }
      
      // Encontrar el número más alto en los códigos existentes
      let maxNumber = 0;
      proyectos.forEach(proyecto => {
        const code = proyecto.Clave;
        if (code && code.startsWith('PR')) {
          const num = parseInt(code.substring(2));
          if (!isNaN(num) && num > maxNumber) {
            maxNumber = num;
          }
        }
      });
      
      return `PR${String(maxNumber + 1).padStart(3, '0')}`;
    },
    submitForm() {
      // Asegurar que la empresa esté capturada antes de enviar
      this.ensureEmpresaCaptured();
      
      // Preparar datos para envío
      const dataToSubmit = { ...this.formData };
      
      // Si es un nuevo registro, generar código secuencial
      if (!this.isEditing && !dataToSubmit.Clave) {
        // Obtener los proyectos actuales del servicio padre
        const proyectos = this.$parent.service?.proyectos || [];
        dataToSubmit.Clave = this.generateCode(proyectos);
      }
      
      // Convertir fechas si existen
      if (dataToSubmit.FechaInicio) {
        try {
          const date = new Date(dataToSubmit.FechaInicio);
          if (!isNaN(date.getTime())) {
            dataToSubmit.FechaInicio = date.toISOString();
          } else {
            dataToSubmit.FechaInicio = null;
          }
        } catch (error) {
          dataToSubmit.FechaInicio = null;
        }
      } else {
        dataToSubmit.FechaInicio = null;
      }
      
      if (dataToSubmit.FechaFin) {
        try {
          const date = new Date(dataToSubmit.FechaFin);
          if (!isNaN(date.getTime())) {
            dataToSubmit.FechaFin = date.toISOString();
          } else {
            dataToSubmit.FechaFin = null;
          }
        } catch (error) {
          dataToSubmit.FechaFin = null;
        }
      } else {
        dataToSubmit.FechaFin = null;
      }
      
      // Convertir números si existen
      if (dataToSubmit.Presupuesto !== null && dataToSubmit.Presupuesto !== '') {
        dataToSubmit.Presupuesto = parseFloat(dataToSubmit.Presupuesto);
      } else {
        dataToSubmit.Presupuesto = 0;
      }
      
      if (dataToSubmit.CostoActual !== null && dataToSubmit.CostoActual !== '') {
        dataToSubmit.CostoActual = parseFloat(dataToSubmit.CostoActual);
      } else {
        dataToSubmit.CostoActual = 0;
      }
      
      if (dataToSubmit.PorcentajeAvance !== null && dataToSubmit.PorcentajeAvance !== '') {
        dataToSubmit.PorcentajeAvance = parseFloat(dataToSubmit.PorcentajeAvance);
      } else {
        dataToSubmit.PorcentajeAvance = 0;
      }
      
      this.$emit('submit', dataToSubmit);
    }
  }
};
</script>

<style scoped>
/* Estilos específicos del formulario si son necesarios */
</style>